import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {AddItemButton} from '@Components/poster-editor/components/poster-add-item-bar/components/add-item-button';
import type {AddItemOptionsProps} from '@Components/poster-editor/components/poster-add-item-bar/poster-add-item-bar.types';
import {ADD_ITEM_BAR_POPUP_OFFSET, ADD_ITEM_BAR_TOP_OFFSET} from '@Components/poster-editor/components/poster-add-item-bar/poster-add-item-bar.types';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {AddItemThumbnail} from '@Components/poster-editor/components/poster-add-item-bar/components/add-item-thumbnail';
import {addQrCodeToPage} from '@PosterWhiteboard/items/qr-code-item.class';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {LayoutDropdownList} from '@Components/poster-editor/components/poster-add-item-bar/components/layout-dropdown-list';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {RecordDropdownList} from '@Components/poster-editor/components/poster-add-item-bar/components/record-dropdown-list/record-dropdown-list';
import {SlideshowDropdownList} from '@Components/poster-editor/components/poster-add-item-bar/components/slideshow-dropdown-list/slideshow-dropdown-list';
import {BackgroundDropdownList} from '@Components/poster-editor/components/poster-add-item-bar/components/background-dropdown-list';
import {updateRecordOptionsOpenState} from '@Components/poster-editor/poster-editor-reducer';

enum MoreOptions {
  NONE = 'none',
  SLIDESHOW = 'slideshow',
  LAYOUT = 'layout',
  RECORD = 'record',
  BACKGROUND = 'background',
}

interface MoreOptionsButtonProps extends AddItemOptionsProps {
  showQROption: boolean;
  showSlideshowOption: boolean;
  showDrawOption: boolean;
  showRecordOption: boolean;
  showLayoutOption: boolean;
  showBackgroundOption: boolean;
}

export function MoreOptionsButton({popupClassName = '', arrowClasses = '', ...props}: MoreOptionsButtonProps): ReactElement<any> | null {
  const [isOpened, setIsOpened] = useState(false);
  const [selectedOption, setSelectedOption] = useState(MoreOptions.NONE);
  const ref = useRef<HTMLDivElement>(null);
  const isDrawModeActive = useAppSelector((state) => {
    return state.posterEditor.posterObject?.drawing.isDrawModeOn;
  });
  const isImageBeingGenerated = useAppSelector((state) => {
    return state.posterEditor.posterObject?.drawing.isImageBeingGenerated;
  });
  const triggerOpenRecordOptions = useAppSelector((state) => {
    return state.posterEditor.triggerOpenRecordOptions;
  });
  const dispatch = useAppDispatch();

  const openRecordOptions = (): void => {
    if (!triggerOpenRecordOptions) {
      setSelectedOption(MoreOptions.NONE);
      setIsOpened(false);
      return;
    }
    setSelectedOption(MoreOptions.RECORD);
    setIsOpened(true);
  };

  useEffect(() => {
    if (props.showRecordOption) {
      openRecordOptions();
    }
  }, [triggerOpenRecordOptions]);

  if (!props.showQROption) {
    return null;
  }

  const onDrawButtonClicked = (): void => {
    closeDropdown();
    window.posterEditor?.whiteboard?.drawing.updateFromObject({
      isDrawModeOn: !isDrawModeActive,
    });
  };

  const closeDropdown = (): void => {
    setSelectedOption(MoreOptions.NONE);
    if (ref.current) {
      $(ref.current).trigger('click');
    }
  };

  const getListOptions = (): ControlledListItemType[] => {
    return [
      {
        id: 'background-tool-button',
        className: props.showBackgroundOption ? '' : '_hidden',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        text: window.i18next.t('pmwjs_background'),
        subText: window.i18next.t('pmwjs_add_background_description'),
        onClick: (): void => {
          setSelectedOption(MoreOptions.BACKGROUND);
        },
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: <AddItemThumbnail icon="icon-background" />,
        },
      },
      {
        id: 'layout-tool-button',
        className: props.showLayoutOption ? '' : '_hidden',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        text: window.i18next.t('pmwjs_layout'),
        subText: window.i18next.t('pmwjs_add_layout_description'),
        onClick: (): void => {
          setSelectedOption(MoreOptions.LAYOUT);
        },
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: <AddItemThumbnail icon="icon-layout" />,
        },
      },
      {
        id: 'record-tool-button',
        className: props.showRecordOption ? '' : '_hidden',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        text: window.i18next.t('pmwjs_record'),
        subText: window.i18next.t('pmwjs_record_description'),
        onClick: (): void => {
          setSelectedOption(MoreOptions.RECORD);
        },
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: <AddItemThumbnail icon="icon-radio-button-checked" />,
        },
      },
      {
        id: 'draw-tool-button',
        className: props.showDrawOption ? '' : '_hidden',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        text: window.i18next.t('pmwjs_draw'),
        isDisabled: isImageBeingGenerated,
        isSelected: isDrawModeActive,
        subText: window.i18next.t('pmwjs_freehand_drawing_tool'),
        onClick: onDrawButtonClicked,
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: <AddItemThumbnail icon="icon-pencil" />,
        },
      },
      {
        id: 'slideshow-tool-button',
        className: props.showSlideshowOption ? '' : '_hidden',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        text: window.i18next.t('pmwjs_slideshow'),
        subText: window.i18next.t('pmwjs_add_slideshow'),
        onClick: (): void => {
          setSelectedOption(MoreOptions.SLIDESHOW);
        },
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: <AddItemThumbnail icon="icon-slideshow" />,
        },
      },
      {
        id: 'qrcode-tool-button',
        className: props.showQROption ? '' : '_hidden',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        text: window.i18next.t('pmwjs_qr_code'),
        subText: window.i18next.t('pmwjs_add_custom_qrcode'),
        onClick: (): void => {
          closeDropdown();
          addQrCodeToPage();
        },
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: <AddItemThumbnail icon="icon-qr-content" />,
        },
      },
    ];
  };

  const shouldRenderBackButtonList = (): boolean => {
    return selectedOption !== MoreOptions.NONE;
  };

  const getOptions = (): ReactElement<any> => {
    return (
      <div key="more-options">
        <Button
          size={Size.SMALL}
          type={Type.SECONDARY}
          onClick={(): void => {
            setSelectedOption(MoreOptions.NONE);
          }}
          isFullWidth
          customClasses={`spacing-m-b-2 ${shouldRenderBackButtonList() ? '' : '_hidden'}`}
          text={window.i18next.t('pmwjs_back')}
          icon="icon-back"
          iconPlacement={IconPlacement.LEFT}
        />
        <ControlledList className={`spacing-m-0 ${selectedOption !== MoreOptions.NONE ? '_hidden' : ''}`} items={getListOptions()} />
        <BackgroundDropdownList
          onOpenModal={(): void => {
            closeDropdown();
          }}
          className={`spacing-m-0 ${selectedOption !== MoreOptions.BACKGROUND ? '_hidden' : ''}`}
        />
        <SlideshowDropdownList
          onOpenModal={(): void => {
            closeDropdown();
          }}
          className={`spacing-m-0 ${selectedOption !== MoreOptions.SLIDESHOW ? '_hidden' : ''}`}
        />
        <RecordDropdownList
          onOpenModal={(): void => {
            closeDropdown();
          }}
          className={`spacing-m-0 ${selectedOption !== MoreOptions.RECORD ? '_hidden' : ''}`}
        />
        <LayoutDropdownList
          onOpenModal={(): void => {
            closeDropdown();
          }}
          className={selectedOption !== MoreOptions.LAYOUT ? '_hidden' : ''}
        />
      </div>
    );
  };

  return (
    <Dropdown
      open={isOpened}
      id="toolbar-more-options"
      key="toolbar-more-options"
      doCloseOnInnerClick={false}
      alignment={ALIGNMENT_TYPE.RIGHT_END}
      selector={
        <div ref={ref}>
          <AddItemButton isSelected={isOpened || (props.showDrawOption && isDrawModeActive)} text={window.i18next.t('pmwjs_more')} icon="icon-more-h" />
        </div>
      }
      popup={getOptions()}
      popUpHasCustomHeight
      maxHeight={500}
      popUpHasCustomWidth
      useDefaultArrow
      popupOffset={ADD_ITEM_BAR_POPUP_OFFSET}
      topOffset={ADD_ITEM_BAR_TOP_OFFSET}
      position={DROPDOWN_POSITION.FIXED}
      popupClassName={popupClassName}
      arrowClasses={arrowClasses}
      onOpen={(): void => {
        if (!isOpened) {
          trackPosterBuilderGA4Events(GA4EventName.MORE_OPTIONS_OPEN);
          setIsOpened(true);
        }
      }}
      onClose={(): void => {
        if (isOpened) {
          setIsOpened(false);
          dispatch(updateRecordOptionsOpenState(false));
          ref.current?.blur();
          setSelectedOption(MoreOptions.NONE);
        }
      }}
    />
  );
}
